import { AfterViewInit, Component, inject, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InfoboxService } from '@/common/infobox/infobox.service';

import {
	Nationality,
	Organization,
	OwnerUser,
	PatchUserDataFullPayload,
	Reservation,
} from '../../../reservation.model';

import { ReservationService } from '../../../reservation.service';
import { ReservationSummaryApiService } from '../../services/reservation-summary-api.service';
import { ContactDestiny } from '@/common/enums/ContactDestiny';
import { LoaderService } from '@/common/loader/loader.service';
import { DateService } from '@/common/date.service';
import { DuplicateUserDataDialogComponent } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/duplicate-user-data-dialog/duplicate-user-data-dialog.component';
import { createQueryParams } from '@/_utils/createQueryParams';
import { finalize } from 'rxjs';
import { DatePipe } from '@angular/common';

export const USER_ALREADY_EXISTS_ERROR = '["User with this data already exists."]';

export type DialogData = {
	reservation: Reservation;
	ownerData: OwnerUser | Organization;
};

export interface ReservationData {
	ownerUser: OwnerUser;
	ownerOrganization: number;
	ownerNip: string;
	reservationId: number;
	invoiceNumber: string;
	ownerPhone: string;
	ownerEmail: string;
	ownerZipCode: string;
	ownerCity: string;
	ownerStreet: string;
}

export enum CustomerType {
	PRIVATE = 0,
	COMPANY = 1,
}

@Component({
	selector: 'app-reservation-summary-owner-dialog',
	templateUrl: './reservation-summary-owner-dialog.component.html',
	styleUrls: ['./reservation-summary-owner-dialog.component.scss'],
})
export class ReservationSummaryOwnerDialogComponent implements OnInit, AfterViewInit {
	data: ReservationData = {
		ownerUser: null,
		ownerOrganization: null,
		reservationId: null,
		ownerNip: '',
		invoiceNumber: '',
		ownerPhone: '',
		ownerEmail: '',
		ownerZipCode: '',
		ownerCity: '',
		ownerStreet: '',
	};

	nationalities: Nationality[] = [];

	formGroup = new FormGroup({
		sex: new FormControl('', [Validators.required]),
		firstName: new FormControl('', [Validators.required]),
		lastName: new FormControl('', [Validators.required]),
		birth: new FormControl(null),
		nationality: new FormControl(null),
		idNumber: new FormControl('', [Validators.pattern('^[A-Za-z]{3}\\s?\\d{6}$')]),
		passportNumber: new FormControl('', [Validators.pattern('^[A-Za-z]{2}\\s?\\d{7}$')]),
		phoneNumber: new FormControl('', [
			Validators.required,
			Validators.pattern('^\\+\\d{2,3}(?:(?:\\s\\d{3}){3}|\\d{9})$'),
		]),
		email: new FormControl('', [Validators.pattern('[^@ \\t\\r\\n]+@[^@ \\t\\r\\n]+\\.[^@ \\t\\r\\n]+')]),
		city: new FormControl('', []),
		street: new FormControl('', []),
		zipCode: new FormControl('', [Validators.pattern('^[0-9]{2}-[0-9]{3}')]),
	});

	constructor(@Inject(MAT_DIALOG_DATA) public dialogData: DialogData) {}

	readonly _service = inject(ReservationService);

	private readonly infobox = inject(InfoboxService);
	private readonly dialogRef = inject(MatDialogRef<ReservationSummaryOwnerDialogComponent>);
	private readonly apiService = inject(ReservationSummaryApiService);
	private readonly loader = inject(LoaderService);
	private readonly dateService = inject(DateService);
	private readonly dialog = inject(MatDialog);
	private readonly datePipe = inject(DatePipe);

	ngOnInit(): void {
		if (this.dialogData.ownerData) {
			// Remove reactivity
			this.data.ownerUser = JSON.parse(JSON.stringify(this.dialogData.ownerData as OwnerUser));
		}

		this.data.reservationId = this.dialogData.reservation?.id;
		this.data.ownerNip = this.dialogData.reservation?.ownerNip;

		if (this.data.ownerUser && this.data.ownerUser.userContacts.length) {
			this.data.ownerEmail = this.data.ownerUser.userContacts[0].email;
			this.data.ownerPhone = this.data.ownerUser.userContacts[0].phone;
		}

		if (this.data.ownerUser && this.data.ownerUser.userAddresses.length) {
			this.data.ownerZipCode = this.data.ownerUser.userAddresses[0].zipCode;

			this.data.ownerCity = this.data.ownerUser.userAddresses[0].city;

			this.data.ownerStreet = this.data.ownerUser.userAddresses[0].street;
		}

		this._service.loadUsers();
		this.nationalities = this._service.nationalities;
	}

	ngAfterViewInit(): void {
		if (this.dialogData.ownerData) {
			this.updateFormGroup(this.data.ownerUser);
		}
	}

	getControlValue<T>(controlName: string): T {
		return this.formGroup.controls[controlName].value;
	}

	submit(): void {
		let ownerUser: PatchUserDataFullPayload = null;
		this.formGroup.markAllAsTouched();

		if (this.data.ownerUser && this.formGroup.valid) {
			const birth = this.getControlValue<string>('birth');
			let date = null;

			if (birth) {
				const datetime = new Date(birth);
				date = this.dateService.date2iso(datetime.toISOString()).split(' ')[0];
			}

			const { patchUserDataFull } = this.apiService.actions();
			ownerUser = {
				id: this.data.ownerUser.id,
				regularCustomer: this.data.ownerUser.regularCustomer,
				firstName: this.getControlValue<string>('firstName'),
				lastName: this.getControlValue<string>('lastName'),
				idNumber: this.getControlValue<string>('idNumber')?.split(' ').join(''),
				passportNumber: this.getControlValue<string>('passportNumber')?.split(' ').join(''),
				sex: this.getControlValue<string>('sex') as string,
				birth: date,
				nationality: this.getControlValue<number>('nationality'),
				userContacts: [
					{
						phone: this.getControlValue<string>('phoneNumber').split(' ').join(''),
						email: this.getControlValue<string>('email'),
						destiny: this.data.ownerUser.userContacts.length
							? this.data.ownerUser.userContacts[0].destiny.id
							: ContactDestiny.CONTACT,
					},
				],
				userConsent: this.data.ownerUser.userConsent.map((v) => ({
					...v,
					consent: v.consent.id,
				})),
				userAddresses: [
					{
						street: this.getControlValue<string>('street'),
						city: this.getControlValue<string>('city'),
						zipCode: this.getControlValue<string>('zipCode'),
					},
				],
			};

			this.loader.show('patch-userdata');
			patchUserDataFull(ownerUser)
				.then(() => {
					this.infobox.success('Pomyślnie zmieniono dane zgłaszającego');
					this._service.loadReservation(this.data.reservationId);
					this.dialogRef.close();
				})
				.catch((err) => {
					if (err === USER_ALREADY_EXISTS_ERROR) {
						this.openDuplicateUserDataDialog();
					}
				})
				.finally(() => {
					this.loader.hide('patch-userdata');
				});
		}
	}

	controlFilledIncorrectly(controlName: string): boolean {
		return this.formGroup.get(controlName).invalid && this.formGroup.get(controlName).value;
	}

	get dialogTitle(): string {
		return this.data.ownerUser ? 'Zmień dane zgłaszającego' : 'Zmień zgłaszającego';
	}

	closeDialog(): void {
		this.data.ownerUser = this.dialogData.ownerData as OwnerUser;
		this.dialogRef.close();
	}

	updateFormGroup(ownerUser: OwnerUser): void {
		if (!ownerUser) {
			return;
		}

		this.formGroup.patchValue({
			sex: ownerUser.sex,
			firstName: ownerUser.firstName,
			lastName: ownerUser.lastName,
			birth: ownerUser.birth,
			nationality: ownerUser.nationality ? ownerUser.nationality.id : null,
			idNumber: ownerUser.idNumber,
			passportNumber: ownerUser.passportNumber,
			email: ownerUser.userContacts.length ? ownerUser.userContacts[0].email : null,
			phoneNumber: ownerUser.userContacts.length ? ownerUser.userContacts[0].phone : null,
			street:
				ownerUser.userAddresses.length && ownerUser.userAddresses[0].street
					? ownerUser.userAddresses[0].street
					: null,
			city:
				ownerUser.userAddresses.length && ownerUser.userAddresses[0].city
					? ownerUser.userAddresses[0].city
					: null,
			zipCode: ownerUser.userAddresses.length ? ownerUser.userAddresses[0].zipCode : null,
		});
	}

	private openDuplicateUserDataDialog(): void {
		this.loader.show('data-full');

		const dataFilters = this.buildDataFullFilters();

		const queryParams = createQueryParams(dataFilters);

		this.apiService
			.getDataFull(queryParams)
			.pipe(finalize(() => this.loader.hide('data-full')))
			.subscribe((users) =>
				this.dialog.open(DuplicateUserDataDialogComponent, {
					width: '65%',
					data: { ownerUsers: users, slotId: null },
				}),
			);
	}

	private buildDataFullFilters(): Record<string, string> {
		return {
			birth: this.getEncodedBirth(),
			email: this.getEncodedValue('email'),
			full_name: this.getEncodedFullName(),
			phone: this.getEncodedValue('phoneNumber'),
		};
	}

	private getEncodedValue(controlName: string): string {
		const value = this.formGroup.get(controlName)?.value ?? '';
		return encodeURIComponent(value);
	}

	private getEncodedBirth(): string {
		const birth = this.formGroup.get('birth')?.value ?? '';

		return encodeURIComponent(this.datePipe.transform(birth, 'yyyy-MM-dd')) ?? '';
	}

	private getEncodedFullName(): string {
		const firstName = this.formGroup.get('firstName')?.value ?? '';
		const lastName = this.formGroup.get('lastName')?.value ?? '';
		const fullName = `${firstName} ${lastName}`.trim();
		return encodeURIComponent(fullName);
	}
}
